// WHAT: Visualization step component with textarea for describing visualized goals
// WHY:  Provides interface for users to document their visualization practice and goals
// DOMAIN: Productivity → Miracle Flow → Session Steps
// DEPENDENCIES: Textarea component, Stack layout, React controlled inputs
// RISKS:
//   - NOTE: Textarea value is controlled by parent - may lose data on unmount
//   - NOTE: No validation for empty visualization - users may skip this step
//   - NOTE: Visualization content is unstructured - may be difficult to analyze later

import { Target } from "lucide-react";
import React from "react";

import { Stack, Heading, Textarea } from "@/components/ui";
import { cn } from "@/shared/utils";

export interface VisualizationStepProperties {
  className?: string;
  onChange: (value: string) => void;
  value: string;
}

/**
 * WHAT:
 *   Visualization step component with textarea for describing visualized goals and future states.
 *
 * WHY:
 *   Provides interface for users to document their visualization practice and goals during session.
 *
 * PROPS:
 *   - value: string — Current visualization text (controlled by parent)
 *   - onChange: (value: string) => void — Callback for text changes
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Displays large textarea for visualization description input
 *   - Uses purple color scheme to represent imagination/vision
 *   - Provides placeholder text encouraging detailed descriptions
 *   - Shows helper text emphasizing importance of vivid visualization
 *   - All state is controlled by parent component
 *
 * RISKS:
 *   - NOTE: Textarea value is controlled by parent - may lose data on unmount if not saved
 *   - NOTE: No validation for empty visualization - users may skip this step
 *   - NOTE: Visualization content is unstructured - may be difficult to analyze later
 *   - WARN: No auto-save mechanism - data loss risk on navigation
 *
 * @param root0
 *
 * @param root0.value
 *
 * @param root0.onChange
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const VisualizationStep: React.FC<VisualizationStepProperties> = ({
  className,
  onChange,
  value,
}) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-purple-500 to-purple-600 shadow-tracq-card">
          <Target className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-purple-400" level="h3" size="xl">
            Visualization
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Close your eyes and visualize your goals. See yourself achieving what you desire.
          </p>
        </Stack>
      </Stack>

      {/* Visualization Textarea */}
      <div className="space-y-3 w-full">
        <label className="block text-sm font-semibold text-[#CBD5E1]">
          Describe what you visualized...
        </label>
        <Textarea
          className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[200px] text-base leading-relaxed w-full"
          onChange={(value: string) => {
            onChange(value);
          }}
          placeholder="Close your eyes and imagine your future. What do you see? What are you achieving? How does it feel? Write down every detail..."
          rows={8}
          value={value}
        />
        <p className="text-[#94A3B8] text-xs">
          Be as detailed as possible. The more vivid your visualization, the more powerful it
          becomes.
        </p>
      </div>
    </div>
  );
};
